import { mod } from "@common/utils/math";
import classNames from "classnames";
import { observer } from "mobx-react";
import type { FC } from "react";
import { useEffect, useState } from "react";
import type { TodoHotkeyConfig } from "@web/src/app/hooks/useHotkeys";
import { useStores } from "../../hooks/useStore";
import { IMPERATIVE_MODAL_CTX, useCtxHotkeys } from "../hooks/useCtxHotkeys";
import type { ButtonProps } from "./button";
import { Button } from "./button";
import type { ModalAction } from "@web/src/app/entities/imperative-modal";
import * as Dialog from "@radix-ui/react-dialog";

export const buttonPropsMap: Record<
  ModalAction["intent"],
  { active: Pick<ButtonProps, "color" | "variant" | "mode">; inactive: Pick<ButtonProps, "color" | "variant" | "mode"> }
> = {
  PRIMARY: {
    active: {
      color: "primary",
      variant: "solid",
      mode: "contrast",
    },
    inactive: {
      color: "primary",
      variant: "transparent",
    },
  },
  DANGER: {
    active: {
      color: "red",
      variant: "solid",
    },
    inactive: {
      color: "red",
      variant: "transparent",
    },
  },
  CANCEL: {
    active: {
      color: "secondary",
      variant: "solid",
    },
    inactive: {
      color: "secondary",
      variant: "transparent",
    },
  },
  WARNING: {
    active: {
      color: "yellow",
      variant: "solid",
    },
    inactive: {
      color: "yellow",
      variant: "transparent",
    },
  },
};

export const buttonColorMap: Record<ModalAction["intent"], ButtonProps["color"]> = {
  PRIMARY: "primary",
  DANGER: "red",
  CANCEL: "secondary",
  WARNING: "yellow",
};

const ImperativeModal: FC = observer(() => {
  const { uiStore } = useStores();
  const { modal } = uiStore;
  const [loadingActionIndex, setLoadingActionIndex] = useState(-1);
  const isActionLoading = loadingActionIndex !== -1;

  function dismiss() {
    uiStore.modal?.onDismiss?.();
    uiStore.setModalProps(undefined);
  }

  const [curModalActionIdx, setCurModalActionIdx] = useState(0);

  useEffect(() => {
    if (!uiStore.hasModal) return;
    // reset loading state
    setLoadingActionIndex(-1);
  }, [uiStore.hasModal]);

  useEffect(() => {
    if (uiStore.hasModal && uiStore.modal) {
      if (uiStore.modal.actions.filter((action) => action.default).length > 0) {
        setCurModalActionIdx(uiStore.modal.actions.findIndex((action) => action.default));
      } else {
        setCurModalActionIdx(0);
      }
    }
  }, [uiStore.hasModal, uiStore.modal]);

  async function onConfirm(index: number) {
    try {
      setLoadingActionIndex(index);
      const onClick = uiStore.modal?.actions[index]?.onClick;

      // IMPORTANT: dismiss first, then trigger onClick, as there may be chained modals
      // that will trigger within onClick
      dismiss();

      await onClick?.();
    } catch (error) {
      setLoadingActionIndex(-1);
      dismiss();
    }
  }

  const isHorizontal = (modal?.actions?.length ?? 0) <= 2;
  const hotkeyConfig: TodoHotkeyConfig[] = [
    {
      combo: isHorizontal ? "left" : "up",
      label: isHorizontal ? "left" : "up",
      onKeyDown: () => {
        setCurModalActionIdx((prev) => mod(prev - 1, uiStore.modal?.actions.length ?? 0));
      },
      preventDefault: false,
      stopPropagation: false,
    },
    {
      combo: isHorizontal ? "right" : "down",
      label: isHorizontal ? "right" : "down",
      onKeyDown: () => {
        setCurModalActionIdx((prev) => mod(prev + 1, uiStore.modal?.actions.length ?? 0));
      },
      preventDefault: false,
      stopPropagation: false,
    },
    {
      combo: "enter",
      label: "enter",
      onKeyDown: () => onConfirm(curModalActionIdx),
      preventDefault: false,
      stopPropagation: false,
    },
    {
      combo: "esc",
      label: "esc",
      onKeyDown: () => {
        if (isActionLoading) return; // cannot dismiss while action pending
        dismiss();
      },
      preventDefault: false,
      stopPropagation: false,
    },
  ];
  useCtxHotkeys(hotkeyConfig, IMPERATIVE_MODAL_CTX, uiStore.hasModal);
  if (!modal) {
    return <></>;
  }

  const { content: modalContent, title: modalTitle, actions, ...otherProps } = modal;

  return (
    <Dialog.Root
      {...otherProps}
      onOpenChange={(open) => {
        if (isActionLoading) return; // cannot dismiss while action pending
        !open && dismiss();
      }}
      open={uiStore.hasModal}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <div className="p-4 font-sans bg-surface-prm">
            <div className="flex flex-col items-center justify-center h-full">
              <div className="text-md font-bold py-2 text-glyph-prm">{modalTitle.toUpperCase()}</div>
              {typeof modalContent === "string" ? (
                <div className="text-md font-semibold text-glyph-scd text-center max-w-sm whitespace-pre-wrap">
                  {modalContent}
                </div>
              ) : (
                modalContent
              )}
              <div
                className={classNames("flex mt-4", {
                  "flex-row": isHorizontal,
                  "flex-col": !isHorizontal,
                })}
              >
                {actions.map((action, idx) => {
                  const isCurrent = idx === curModalActionIdx;
                  const isLoading = idx === loadingActionIndex;
                  return (
                    <Button
                      disabled={isActionLoading}
                      key={idx}
                      className={classNames("flex justify-center items-center", {
                        "mx-1 py-1 px-3": isHorizontal,
                        "py-1 px-2": !isHorizontal,
                      })}
                      {...buttonPropsMap[action.intent][isCurrent ? "active" : "inactive"]}
                      style={{ transition: "0.2s", transform: isCurrent ? `scale(1.06)` : "" }}
                      onClick={() => onConfirm(idx)}
                      mode={isCurrent ? "contrast" : "default"}
                      isLoading={isLoading}
                    >
                      {action.text}
                    </Button>
                  );
                })}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
});

export default ImperativeModal;
